import React, { useState } from 'react';
import {
  Box, Flex, Heading, Text, HStack, Button, ButtonGroup, IconButton, Tooltip, Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { BookOpen, Play, Pause, SkipBack, SkipForward, X } from 'lucide-react';
import Sidebar from '../Dashboard/Sidebar';

const TABS = [
  { label: 'Surah', path: '/surahs' },
  { label: 'Juz', path: '/juz' },
  { label: 'Page', path: '/pages' },
];

const PlayerBar = ({ player, left }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  if (!player.current) return null;

  const playing = player.status === 'playing';
  const statusText = {
    loading: 'Loading…',
    error: 'This recording could not be played',
    paused: 'Paused',
  }[player.status];

  return (
    <Box
      position="fixed" bottom={0} left={left} right={0} zIndex={20}
      bg={bg} borderTop="1px" borderColor={borderColor} boxShadow="0 -4px 12px rgba(0,0,0,0.06)"
      px={{ base: 4, md: 8 }} py={3} transition="left 0.2s ease"
    >
      <Flex align="center" justify="space-between" gap={4} maxW="1100px" mx="auto">
        <Box minW={0}>
          <Text fontWeight="semibold" noOfLines={1}>{player.current.label}</Text>
          <Text fontSize="xs" color={player.status === 'error' ? 'red.500' : subtextColor} noOfLines={1}>
            {statusText || player.current.sublabel}
          </Text>
        </Box>
        <HStack spacing={1} flexShrink={0}>
          <IconButton aria-label="Previous" icon={<SkipBack size={18} />} variant="ghost" isDisabled={!player.hasPrev} onClick={player.prev} />
          <IconButton
            aria-label={playing ? 'Pause' : 'Play'}
            icon={player.status === 'loading' ? <Spinner size="sm" /> : playing ? <Pause size={20} /> : <Play size={20} />}
            colorScheme="teal" borderRadius="full" size="lg" onClick={player.toggle}
          />
          <IconButton aria-label="Next" icon={<SkipForward size={18} />} variant="ghost" isDisabled={!player.hasNext} onClick={player.next} />
          <Tooltip label="Close player">
            <IconButton aria-label="Close player" icon={<X size={18} />} variant="ghost" onClick={player.stop} />
          </Tooltip>
        </HStack>
      </Flex>
    </Box>
  );
};

/**
 * Layout shared by the Quran reading pages: the app sidebar, a header with the Surah / Juz / Page
 * tabs, and (when a player is passed) a sticky audio bar at the bottom.
 */
const QuranShell = ({ children, player }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const headerBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const sidebarWidth = sidebarCollapsed ? '60px' : '250px';

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <Box pl={sidebarWidth} w="full" pb={player?.current ? 28 : 10} transition="padding-left 0.2s ease">
        <Box bg={headerBg} borderBottom="1px" borderColor={borderColor} px={{ base: 4, md: 8 }} py={4} position="sticky" top={0} zIndex={10}>
          <Flex justify="space-between" align="center" gap={4} wrap="wrap" maxW="1100px" mx="auto">
            <HStack spacing={3} color="teal.600">
              <BookOpen size={24} />
              <Heading size="md">Read Quran</Heading>
            </HStack>
            <ButtonGroup size="sm" isAttached variant="outline" colorScheme="teal">
              {TABS.map((tab) => {
                const active = location.pathname.startsWith(tab.path);
                return (
                  <Button key={tab.path} as={RouterLink} to={tab.path} variant={active ? 'solid' : 'outline'}>
                    {tab.label}
                  </Button>
                );
              })}
            </ButtonGroup>
          </Flex>
        </Box>

        <Box px={{ base: 4, md: 8 }} pt={6} maxW="1100px" mx="auto">
          {children}
        </Box>
      </Box>

      {player && <PlayerBar player={player} left={sidebarWidth} />}
    </Flex>
  );
};

export default QuranShell;
